import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { CheckCircle2, Clock, LinkIcon, MailOpen, XCircle } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type InvitePreview, type InviteStatus } from '@apm/contracts';
import { Button } from '../../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { ErrorState, Skeleton } from '../../components/ui/states';
import { invitationApi, projectInviteApi, projectInviteKeys } from '../../features/projects/api';
import { errorMessage } from '../../lib/error-message';

/**
 * Trang mở link mời — APM-1317b, đổi thành form đăng ký ở APM-ROLE-p.
 *
 * ## Vì sao route này nằm **dưới** `_app`, khác với link chia sẻ báo cáo
 *
 * Link mời chỉ dùng được cho người **đã có tài khoản trong tổ chức** (spec của
 * task nói rõ: nó không tạo tài khoản mới). Nên lớp gác của `_app` là đúng thứ
 * cần ở đây — nó đá người chưa đăng nhập về `/login?next=/invite/<token>`, và
 * họ quay lại đúng trang này sau khi đăng nhập.
 *
 * ## ⚠️ Bấm nút ở đây KHÔNG đưa ai vào dự án
 *
 * Nút gửi một **nguyện vọng**: vai trò mong muốn, khoảng ngày, thời lượng. PM
 * chốt lại cả ba trước khi người này thành thành viên. Nếu bấm là vào thẳng với
 * vai trò tự chọn thì link mời trở thành một đường tự cấp quyền — ai giữ link
 * cũng tự phong mình làm PM.
 */
export const Route = createFileRoute('/_app/invite/$token')({ component: InvitePage });

/** Icon và màu theo kết cục. `ok` là trạng thái duy nhất còn form. */
const TONE: Record<Exclude<InviteStatus, 'ok'>, string> = {
  already_member: 'text-rag-green',
  pending_request: 'text-rag-amber',
  invited: 'text-primary',
  expired: 'text-rag-amber',
  revoked: 'text-rag-amber',
  not_found: 'text-rag-red',
};

function InvitePage(): ReactNode {
  const { t } = useTranslation();
  const { token } = Route.useParams();
  const navigate = useNavigate();

  const preview = useQuery({
    queryKey: projectInviteKeys.preview(token),
    queryFn: () => projectInviteApi.preview(token),
    // Không thử lại: mọi kết cục xấu ở đây (hết hạn, đã thu hồi, sai tổ chức)
    // đều là câu trả lời cuối cùng, thử lại chỉ làm người dùng chờ lâu hơn.
    retry: false,
  });

  if (preview.isPending) return <Skeleton className="h-64 w-full max-w-lg" />;
  if (preview.isError) return <ErrorState message={errorMessage(preview.error, t)} />;

  const data = preview.data;
  const project = data.projectName === null ? '' : `${data.projectKey ?? ''} · ${data.projectName}`;

  return (
    <Card className="w-full max-w-lg">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {data.status === 'ok' ? (
            <LinkIcon aria-hidden className="size-4 text-muted-foreground" />
          ) : data.status === 'already_member' ? (
            <CheckCircle2 aria-hidden className={`size-4 ${TONE[data.status]}`} />
          ) : data.status === 'pending_request' ? (
            <Clock aria-hidden className={`size-4 ${TONE[data.status]}`} />
          ) : data.status === 'invited' ? (
            <MailOpen aria-hidden className={`size-4 ${TONE[data.status]}`} />
          ) : (
            <XCircle aria-hidden className={`size-4 ${TONE[data.status]}`} />
          )}
          {t(`invite.status.${data.status}`)}
        </CardTitle>
        <CardDescription>{t(`invite.help.${data.status}`)}</CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        {project !== '' && <p className="text-sm font-medium">{project}</p>}

        {/* Mục tiêu và thời gian dự án — người ta đang quyết bỏ một phần thời
            gian của mình vào đây, và một cái tên dự án không đủ để quyết. */}
        {data.status !== 'not_found' && (
          <div className="flex flex-col gap-1 text-sm text-muted-foreground">
            <p>{data.projectDescription?.trim() || t('invitations.noDescription')}</p>
            {data.projectStartDate !== null || data.projectEndDate !== null ? (
              <p className="tabular-nums">
                {t('invite.projectPeriod', {
                  from: data.projectStartDate ?? '…',
                  to: data.projectEndDate ?? t('invite.openEnded'),
                })}
              </p>
            ) : null}
          </div>
        )}

        {data.status === 'ok' ? (
          <ApplyForm token={token} preview={data} onStale={() => void preview.refetch()} />
        ) : data.status === 'invited' && data.invitationId !== null ? (
          <AcceptInvitation
            invitationId={data.invitationId}
            projectId={data.projectId}
            onStale={() => void preview.refetch()}
          />
        ) : (
          <Button
            onClick={() =>
              void navigate({
                to: '/projects',
                search:
                  data.status === 'already_member' && data.projectId !== null
                    ? { projectId: data.projectId, tab: 'board' }
                    : {},
              })
            }
          >
            {data.status === 'already_member'
              ? t('invite.openProject')
              : t('invite.backToProjects')}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * PM đã mời đích danh người đang xem — trả lời ngay tại đây (APM-ROLE-p2).
 *
 * Trước đây trang này nói "Bạn đã đăng ký, đang chờ duyệt" cho cả lời mời của
 * PM, nên người được mời ngồi chờ còn PM chờ họ nhận lời. Nhận lời là vào dự án
 * ngay (`RespondToRequestHandler` chép sang `project_membership`), nên xong thì
 * mở thẳng dự án.
 *
 * Chỉ có nút nhận lời: từ chối bắt buộc ghi lý do, và mục "Lời mời của tôi" ở
 * trang chủ đã có đủ ô ấy — một bản sao thứ hai ở đây là hai chỗ phải giữ khớp.
 */
function AcceptInvitation({
  invitationId,
  projectId,
  onStale,
}: {
  invitationId: string;
  projectId: string | null;
  onStale: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const accept = useMutation({
    mutationFn: () => invitationApi.respond(invitationId, { accept: true }),
    onSuccess: () => {
      // Danh sách dự án ở thanh bên và khối lời mời ở trang chủ đều vừa đổi.
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      void queryClient.invalidateQueries({ queryKey: ['me'] });
      void navigate({
        to: '/projects',
        search: projectId === null ? {} : { projectId, tab: 'board' },
      });
    },
    // Lời mời vừa được trả lời ở tab khác, hay PM vừa rút lại: vẽ lại theo trạng thái mới.
    onError: onStale,
  });

  return (
    <div className="flex flex-col gap-2">
      <Button disabled={accept.isPending} onClick={() => accept.mutate()}>
        {t('invite.acceptInvitation')}
      </Button>
      <p className="text-xs text-muted-foreground">{t('invite.declineAtHome')}</p>
    </div>
  );
}

/**
 * Nguyện vọng của người đăng ký: vai trò, khoảng ngày, thời lượng.
 *
 * Khoảng ngày mặc định bằng khoảng của **dự án**. Phần lớn người tham gia là
 * tham gia cả chặng, nên một ô trống bắt ai cũng phải gõ lại đúng thứ đã hiện ở
 * ngay trên; còn người tham gia một đoạn thì họ sửa — sửa một giá trị có sẵn dễ
 * hơn nghĩ ra một giá trị từ đầu.
 */
function ApplyForm({
  token,
  preview,
  onStale,
}: {
  token: string;
  preview: InvitePreview;
  onStale: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [roleId, setRoleId] = useState('');
  const [fromDate, setFromDate] = useState(preview.projectStartDate ?? '');
  const [toDate, setToDate] = useState(preview.projectEndDate ?? '');
  const [percent, setPercent] = useState('100');
  const [sent, setSent] = useState(false);

  // Vai trò mặc định: vai PM đặt sẵn cho link (APM-PSPACE-l), không thì
  // CONTRIBUTOR — đường vào thường gặp nhất, và cùng vai trò mà bản trước của
  // trang này gán cứng.
  useEffect(() => {
    if (roleId !== '') return;
    const fallback =
      preview.roles.find((role) => role.id === preview.defaultRoleId) ??
      preview.roles.find((role) => role.key === 'CONTRIBUTOR') ??
      preview.roles[0] ??
      null;
    if (fallback) setRoleId(fallback.id);
  }, [preview.roles, preview.defaultRoleId, roleId]);

  const apply = useMutation({
    mutationFn: () =>
      projectInviteApi.apply({
        token,
        roleId,
        ...(fromDate ? { fromDate } : {}),
        ...(toDate ? { toDate } : {}),
        ...(percent ? { commitment: { mode: 'percent' as const, value: Number(percent) } } : {}),
      }),
    onSuccess: (result) => {
      // Link chết ngay giữa lúc đọc và lúc bấm (PM vừa thu hồi, hoặc vừa hết
      // hạn). Vẽ lại theo kết cục mới thay vì báo một lỗi đỏ chung.
      if (result.status !== 'ok' && result.status !== 'pending_request') {
        onStale();
        return;
      }
      setSent(true);
    },
  });

  if (sent) {
    return (
      <div className="flex flex-col gap-3">
        <p className="rounded-DEFAULT border border-rag-green/40 bg-rag-green/5 px-3 py-2 text-sm">
          {t('invite.applied')}
        </p>
        <Button variant="outline" onClick={() => void navigate({ to: '/projects', search: {} })}>
          {t('invite.backToProjects')}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <label className="flex flex-col gap-1 text-xs text-muted-foreground">
        {t('invite.wantRole')}
        <select
          value={roleId}
          onChange={(event) => setRoleId(event.target.value)}
          className="h-9 rounded-DEFAULT border border-input bg-background px-2 text-sm text-foreground"
        >
          {preview.roles.map((role) => (
            <option key={role.id} value={role.id}>
              {role.key === null ? role.name : t(`projectSettings.roleName.${role.key}`)}
            </option>
          ))}
        </select>
      </label>

      <div className="flex flex-wrap gap-3">
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          {t('invitations.inviteFrom')}
          <input
            type="date"
            value={fromDate}
            onChange={(event) => setFromDate(event.target.value)}
            className="h-9 rounded-DEFAULT border border-input bg-background px-2 text-sm text-foreground"
          />
        </label>

        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          {t('invitations.inviteTo')}
          <input
            type="date"
            value={toDate}
            onChange={(event) => setToDate(event.target.value)}
            className="h-9 rounded-DEFAULT border border-input bg-background px-2 text-sm text-foreground"
          />
        </label>

        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          {t('invitations.invitePercent')}
          <input
            type="number"
            min={1}
            max={100}
            value={percent}
            onChange={(event) => setPercent(event.target.value)}
            className="h-9 w-24 rounded-DEFAULT border border-input bg-background px-2 text-sm text-foreground"
          />
        </label>
      </div>

      {/* Nói trước rằng đây là nguyện vọng. Người bấm "Tham gia" rồi không thấy
          dự án đâu sẽ tưởng hệ thống nuốt mất thao tác của họ. */}
      <p className="text-xs text-muted-foreground">{t('invite.pmDecides')}</p>

      <div className="flex flex-wrap gap-2">
        <Button onClick={() => apply.mutate()} disabled={apply.isPending || roleId === ''}>
          {apply.isPending ? t('common.saving') : t('invite.apply')}
        </Button>
        <Button variant="outline" onClick={() => void navigate({ to: '/projects', search: {} })}>
          {t('invite.backToProjects')}
        </Button>
      </div>

      {apply.isError && <ErrorState message={errorMessage(apply.error, t)} />}
    </div>
  );
}
